import React, { useEffect, useState } from "react";
import icon1 from "../../../Assets/Icons/database.svg";
import icon2 from "../../../Assets/Icons/Email Sent.svg";
import icon3 from "../../../Assets/Icons/error.svg";
import icon4 from "../../../Assets/Icons/readiness_score.svg";
import AreaChart from "./AreaChart";
import BarChart from "./BarChart";
import GenericTable from "../../../Components/GenericTable";
import "./style.css";
import { ClipLoader } from "react-spinners";
import { message } from "antd";
import {
  getDashboardAPIVolume,
  getDashboardEmailVolume,
  getDashboardLogs,
  getDashboardStats,
} from "../../../API/dashboard";

const Dashboard = () => {
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [loadingAPIVol, setLoadingAPIVol] = useState(false);
  const [loadingEmailVol, setLoadingEmailVol] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [stats, setStats] = useState({});
  const [apiVolume, setApiVolume] = useState({});
  const [emailVolume, setEmailVolume] = useState({});
  const [logs, setLogs] = useState([]);

  const columns = [
    {
      title: "Event ID",
      dataIndex: "event_id",
      key: "event_id",
      enableSearch: true,
    },
    {
      title: "End Point",
      dataIndex: "endpoint",
      key: "endpoint",
      enableSearch: true,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      filters: [
        { text: "GET", value: "GET" },
        { text: "POST", value: "POST" },
        { text: "PUT", value: "PUT" },
        { text: "DELETE", value: "DELETE" },
      ],
      onFilter: (value, record) => record.method === value,
      render: (text) => {
        let className = "";
        switch (text) {
          case "GET":
            className = "method-get";
            break;
          case "POST":
            className = "method-post";
            break;
          case "PUT":
            className = "method-put";
            break;
          case "DELETE":
            className = "method-delete";
            break;
          default:
            className = "";
        }
        return <span className={className}>{text}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status_code",
      key: "status_code",
      filters: [
        { text: "200 OK", value: "200 OK" },
        { text: "401 Unauthorized", value: "401 Unauthorized" },
      ],
      onFilter: (value, record) => record.status_code === value,
      render: (text) => {
        let className = "";
        switch (text) {
          case "200 OK":
            className = "status-200";
            break;
          case "401 Unauthorized":
            className = "status-401";
            break;
          default:
            className = "";
        }
        return <span className={className}>{text}</span>;
      },
    },
    {
      title: "Response Time",
      dataIndex: "response_time_ms",
      key: "response_time_ms",
      sorter: (a, b) => {
        const timeA = parseInt(a.response_time_ms.replace("ms", ""));
        const timeB = parseInt(b.response_time_ms.replace("ms", ""));
        return timeA - timeB;
      },
    },
    {
      title: "Time Stamp",
      dataIndex: "timestamp",
      key: "timestamp",
      sorter: (a, b) => {
        const parseTime = (t) => {
          if (t.includes("mins ago"))
            return parseInt(t.replace(" mins ago", ""));
          if (t.includes("hour"))
            return parseInt(t.replace(" hours ago", "")) * 60;
          return 0;
        };
        return parseTime(a.timestamp) - parseTime(b.timestamp);
      },
    },
  ];

  const getTrendColor = (value) => {
    if (!value) return "#000";
    const trimmed = value.toString().trim();

    if (trimmed.startsWith("-")) return "#F35D37";
    if (trimmed.startsWith("+")) return "#16a34a";
    return "#000";
  };

  const fetchDashboardStats = async () => {
    try {
      setLoadingTabs(true);

      const response = await getDashboardStats();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || {};
        setStats(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch dashboard stats.");
    } finally {
      setLoadingTabs(false);
    }
  };

  const fetchDashboardApiVolume = async () => {
    try {
      setLoadingAPIVol(true);

      const response = await getDashboardAPIVolume();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || {};
        setApiVolume(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch dashboard data API Volume.");
    } finally {
      setLoadingAPIVol(false);
    }
  };

  const fetchDashboardEmailVolume = async () => {
    try {
      setLoadingEmailVol(true);

      const response = await getDashboardEmailVolume();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || {};
        setEmailVolume(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch dashboard data Email Volume.");
    } finally {
      setLoadingEmailVol(false);
    }
  };

  const fetchDashboardLogs = async () => {
    try {
      setLoadingLogs(true);

      const response = await getDashboardLogs();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data || {};
        setLogs(data);
      } else {
        message.error("Unexpected response from server.");
      }
    } catch (error) {
      message.error("Failed to fetch dashboard data Email Volume.");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchDashboardApiVolume();
    fetchDashboardEmailVolume();
    fetchDashboardLogs();
  }, []);

  return (
    <div className="dashboard">
      {loadingTabs ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <ClipLoader size={50} color={"#123abc"} loading={loadingTabs} />
        </div>
      ) : (
        <div className="dashboard-card">
          {[
            {
              title: "Integrated Server",
              value: stats?.integrated_servers?.total,
              subValue: stats?.integrated_servers?.change_pct || "N/A",
              icon: icon1,
              subLabel: "from last week",
            },
            {
              title: "Email Sent",
              value: stats?.emails_sent?.this_week,
              subValue: stats?.emails_sent?.change_pct || "N/A",
              icon: icon2,
              subLabel: "from last week",
            },
            {
              title: "Failed Deliveries",
              value: `${stats?.failed_delivery_rate?.this_week_pct}%`,
              subValue: stats?.failed_delivery_rate?.change_pct || "N/A",
              icon: icon3,
              subLabel: "Improvement",
            },
            {
              title: "Avg Latency",
              value: `${stats?.avg_latency_ms?.this_week}ms`,
              subValue: stats?.avg_latency_ms?.change_pct || "N/A",
              icon: icon4,
              subLabel: "from last week",
            },
          ].map((card, idx) => (
            <div className="card" key={idx}>
              <div className="wrapper">
                <div className="card-title">{card.title}</div>
                <div className="icon">
                  <img src={card.icon} alt="logo" />
                </div>
              </div>
              <div className="card-value">{card.value}</div>
              <div
                className="card-sub"
                style={{ color: getTrendColor(card.subValue) }}
              >
                {card.subValue} <span>{card.subLabel}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="charts">
        {loadingAPIVol ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            className="loader-container"
          >
            <ClipLoader size={50} color={"#123abc"} loading={loadingTabs} />
          </div>
        ) : (
          <div className="area-chart">
            <AreaChart
              labels={apiVolume?.months}
              seriesData={apiVolume?.total}
              colourTheme={["#25CD25"]}
              title="API Volume Over Time"
            />
          </div>
        )}
        {loadingEmailVol ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            className="loader-container"
          >
            <ClipLoader size={50} color={"#123abc"} loading={loadingTabs} />
          </div>
        ) : (
          <div className="bar-chart">
            <BarChart
              labels={emailVolume?.months}
              seriesData={[
                {
                  name: "Email Sent",
                  data: emailVolume?.sent,
                },
                {
                  name: "Email Failed",
                  data: emailVolume?.failed,
                },
              ]}
              colourTheme={["#4928FDF0", "#4928FD61"]}
              title="Email Sent vs Failed"
            />
          </div>
        )}
      </div>
      <div>
        <GenericTable
          title="Recent API Logs"
          dataSource={logs?.results}
          columns={columns}
          loader={loadingLogs}
        />
      </div>
    </div>
  );
};

export default Dashboard;
